import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Progress,
  useColorModeValue,
  SimpleGrid,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';
import { Brain, GitBranch, Zap, Database, Activity } from 'lucide-react';

const ModelTrainingPanel = () => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const modelTraining = [
    {
      name: "SentientIQ Content Adapter",
      type: "Transformer",
      status: "Training",
      progress: 78,
      metrics: {
        accuracy: "92.4%",
        loss: "0.086",
        epoch: "14/20"
      }
    },
    {
      name: "Domain Knowledge Graph",
      type: "Graph Neural Network",
      status: "Evaluating",
      progress: 100,
      metrics: {
        accuracy: "94.1%",
        loss: "0.042",
        epoch: "Complete"
      }
    },
    {
      name: "Clinical Context Engine",
      type: "BERT-based",
      status: "Deployed",
      progress: 100,
      metrics: {
        accuracy: "95.7%",
        loss: "0.031",
        epoch: "Complete"
      }
    }
  ];

  const cortexMetrics = [
    {
      title: "Admin-Clinical Sync",
      value: "96.8%",
      change: 2.4,
      description: "Content adaptation accuracy"
    },
    {
      title: "Knowledge Integration",
      value: "92.3%",
      change: 1.7,
      description: "Cross-domain correlation"
    },
    {
      title: "Governance Compliance",
      value: "100%",
      change: 0,
      description: "Policy adherence score"
    }
  ];

  return (
    <VStack spacing={6} align="stretch">
      {/* Cortex Integration Metrics */}
      <Box>
        <Text fontSize="lg" fontWeight="medium" mb={4}>Dual Cortices Integration</Text>
        <SimpleGrid columns={3} spacing={4}>
          {cortexMetrics.map((metric, idx) => (
            <Box
              key={idx}
              bg={bgColor}
              p={4}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <Stat>
                <StatLabel>{metric.title}</StatLabel>
                <StatNumber color="methodist.blue">{metric.value}</StatNumber>
                <StatHelpText>
                  {metric.change > 0 && '+'}{metric.change}% from last week
                </StatHelpText>
                <Text fontSize="sm" color="gray.500">{metric.description}</Text>
              </Stat>
            </Box>
          ))}
        </SimpleGrid>
      </Box>

      {/* Model Training Status */}
      <Box>
        <Text fontSize="lg" fontWeight="medium" mb={4}>Active Model Training</Text>
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Model</Th>
              <Th>Type</Th>
              <Th>Status</Th>
              <Th>Metrics</Th>
            </Tr>
          </Thead>
          <Tbody>
            {modelTraining.map((model, idx) => (
              <Tr key={idx}>
                <Td>
                  <HStack>
                    <Icon as={Brain} color="methodist.blue" />
                    <Text fontWeight="medium">{model.name}</Text>
                  </HStack>
                </Td>
                <Td>
                  <Badge colorScheme="purple">{model.type}</Badge>
                </Td>
                <Td>
                  <VStack align="start" spacing={1}>
                    <Badge 
                      colorScheme={
                        model.status === 'Deployed' ? 'green' : 
                        model.status === 'Training' ? 'blue' : 
                        'orange'
                      }
                    >
                      {model.status}
                    </Badge>
                    {model.status === 'Training' && (
                      <Progress 
                        value={model.progress} 
                        size="sm" 
                        width="100px" 
                        colorScheme="blue" 
                      />
                    )}
                  </VStack>
                </Td>
                <Td>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="sm">Accuracy: {model.metrics.accuracy}</Text>
                    <Text fontSize="sm">Loss: {model.metrics.loss}</Text>
                    <Text fontSize="sm">Epoch: {model.metrics.epoch}</Text>
                  </VStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </VStack>
  );
};

export default ModelTrainingPanel;
